import React from 'react';

export type AppErrorFallbackProps = {
  error: Error;
  /** Sentry event id (optional, for support correlation) */
  eventId?: string;
  onReload: () => void;
  onHome: () => void;
};

function normalizeError(error: unknown): Error {
  if (error instanceof Error) return error;
  return new Error(typeof error === 'string' ? error : 'Unknown error');
}

/**
 * Bilingual full-screen fallback when the app hits a render error.
 * Used as the `fallback` render for Sentry’s ErrorBoundary at the App root.
 */
export function AppErrorFallback({
  error,
  eventId,
  onReload,
  onHome,
}: AppErrorFallbackProps) {
  let isAr = false;
  try {
    isAr = localStorage.getItem('ahg.lang') === 'ar';
  } catch {
    // localStorage may be unavailable — ignore
  }

  return (
    <main
      className="min-h-screen bg-sand flex items-center justify-center px-4 py-12"
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className="bg-white rounded-3xl p-8 sm:p-12 max-w-lg w-full shadow-soft border border-sea-blue/5 text-center">
        <div className="w-16 h-16 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center mx-auto mb-6 text-3xl font-serif font-bold">
          !
        </div>
        <h1
          className={`font-serif text-2xl sm:text-3xl font-bold text-sea-blue mb-3 ${isAr ? 'font-arabic' : ''}`}
        >
          {isAr ? 'حصل خطأ غير متوقع' : 'Something went wrong'}
        </h1>
        <p
          className={`text-sea-blue/85 mb-8 leading-relaxed ${isAr ? 'font-arabic' : ''}`}
        >
          {isAr
            ? 'حاول إعادة تحميل الصفحة. لو المشكلة استمرت ارجع للرئيسية.'
            : 'Try reloading the page. If the problem persists, head back to the home page.'}
        </p>
        {error.message ? (
          <details className="mb-8 text-left">
            <summary
              className={`text-xs text-sea-blue/50 cursor-pointer hover:text-sea-blue ${isAr ? 'font-arabic' : ''}`}
            >
              {isAr ? 'تفاصيل تقنية' : 'Technical details'}
            </summary>
            <pre
              className="mt-2 text-[11px] text-sea-blue/80 bg-sand/50 p-3 rounded-lg overflow-auto max-h-32 text-left ltr-numerals"
              dir="ltr"
            >
              {error.message}
            </pre>
          </details>
        ) : null}
        {eventId ? (
          <p
            className="text-[11px] text-sea-blue/40 mb-6 font-mono ltr-numerals"
            dir="ltr"
          >
            ID: {eventId}
          </p>
        ) : null}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={onReload}
            className={`bg-sea-blue hover:bg-sea-blue-light text-white px-6 py-3 rounded-xl font-bold transition-colors ${isAr ? 'font-arabic' : ''}`}
          >
            {isAr ? 'إعادة تحميل' : 'Reload page'}
          </button>
          <button
            type="button"
            onClick={onHome}
            className={`bg-sand hover:bg-sand-dark text-sea-blue px-6 py-3 rounded-xl font-bold transition-colors ${isAr ? 'font-arabic' : ''}`}
          >
            {isAr ? 'الرئيسية' : 'Home'}
          </button>
        </div>
      </div>
    </main>
  );
}
